import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, startWith, tap } from 'rxjs/operators';

import { FormUtils, Mapper, NgUtils, StringUtils, TiimePipesModule } from 'tiime-expert-utils';
import {
  SelectComponent,
  TiimeFormModule,
  TiimeInputContainerModule,
  TiimeOptionSelectionChange,
  TiimeSlideToggleModule
} from 'tiime-material';

import { PcgEntryForm } from '@forms/pcg-entry-form';
import { PcgEntryHelper } from '@helpers/pcg-entry.helper';
import { AccountingConfig } from '@models/accounting-config';
import { Company } from '@models/company';
import { PcgEntry } from '@models/pcg-entry';
import { VatTypeRateModule } from '@modules/shared/components/vat-type-rate/vat-type-rate.module';
import { FormFocusObserveDirective } from '@modules/shared/directives/form-focus-observe/form-focus-observe.directive';
import { FormUpdateOnBlurDirective } from '@modules/shared/directives/form-update-on-blur/form-update-on-blur.directive';
import { CentralizerPcgEntrySelectComponent } from '@modules/shared/selectors/centralizer-pcg-entry-select/centralizer-pcg-entry-select.component';
import { VatExonerationTypeRequiredPipe } from '@shared-pipes/vat-exoneration-type-required/vat-exoneration-type-required.pipe';

@UntilDestroy({ checkProperties: true })
@Component({
  standalone: true,
  selector: 'app-pcg-entry-form',
  templateUrl: './pcg-entry-form.component.html',
  styleUrls: ['./pcg-entry-form.component.scss'],
  imports: [
    VatTypeRateModule,
    VatExonerationTypeRequiredPipe,
    FormFocusObserveDirective,
    FormUpdateOnBlurDirective,
    TiimePipesModule,
    CentralizerPcgEntrySelectComponent,
    ReactiveFormsModule,
    TiimeFormModule,
    TiimeInputContainerModule,
    TiimeSlideToggleModule
  ]
})
export class PcgEntryFormComponent implements OnChanges, AfterViewInit {
  @Input() company: Company;
  @Input() accountingConfig: AccountingConfig;
  @Input('disableCentralizer') set setDisableCentralizer(disableCentralizer: boolean) {
    this.disableCentralizer = disableCentralizer;
    this.manageCentralizerControl(disableCentralizer);
  }
  @Input('pcgEntries') set setPcgEntries(pcgEntries: PcgEntry[]) {
    if (pcgEntries) {
      this.pcgEntries = pcgEntries;
      this.setCentralizerPcgEntriesFromPcgEntries();
    }
  }
  @Input() pcgEntryForm: PcgEntryForm;

  @Output() readonly centralizerCreated: EventEmitter<void> = new EventEmitter<void>();
  @Output() readonly pcgEntryFormChange: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('accountNumberInput', { static: true })
  accountNumberInput: ElementRef;
  @ViewChild('centralizerPcgEntrySelectComponent')
  centralizerPcgEntrySelectComponent: SelectComponent;

  centralizerPcgEntries: PcgEntry[];
  filteredCentralizerPcgEntries: PcgEntry[];
  searchPcgEntriesControl = new UntypedFormControl();

  readonly trackById = NgUtils.trackById;

  private disableCentralizer: boolean;
  private pcgEntries: PcgEntry[];
  private accountNumberChangeSubscription: Subscription;

  readonly mapToCentralizerPcgEntryRequired: Mapper<string, boolean> = accountNumber =>
    accountNumber?.length > 0 && PcgEntryHelper.isAuxiliaryAccountNumber(accountNumber);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pcgEntryForm) {
      this.observeAccountNumberChange();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.accountNumberInput.nativeElement.focus());
  }

  emitPcgEntryFormChange(): void {
    this.pcgEntryFormChange.emit();
  }

  onPcgEntryFormChange(): void {
    if (!this.pcgEntryForm.accountNumber.valid) {
      return;
    }

    if (!this.pcgEntryForm.centralizer.value) {
      const accountNumber = StringUtils.normalizeToLowerCase(this.pcgEntryForm.accountNumber.value);
      if (
        this.accountingConfig &&
        this.accountingConfig.purchasesAccountCentralizerPcgEntry &&
        accountNumber.startsWith(StringUtils.normalizeToLowerCase(this.company.purchasesAccountNumberPrefix))
      ) {
        this.pcgEntryForm.centralizerPcgEntry.patchValue({
          id: this.accountingConfig.purchasesAccountCentralizerPcgEntry.id
        });
      } else if (
        this.accountingConfig &&
        this.accountingConfig.salesAccountCentralizerPcgEntry &&
        accountNumber.startsWith(StringUtils.normalizeToLowerCase(this.company.salesAccountNumberPrefix))
      ) {
        this.pcgEntryForm.centralizerPcgEntry.patchValue({
          id: this.accountingConfig.salesAccountCentralizerPcgEntry.id
        });
      }
    } else {
      this.pcgEntryForm.centralizerPcgEntry.reset();
    }

    this.emitPcgEntryFormChange();
  }

  onBlurAccountNumber(): void {
    this.pcgEntryForm.accountNumber.completeAccountNumber();
  }

  onPcgEntryCreated(pcgEntry: PcgEntry): void {
    this.pcgEntries.push(pcgEntry);
    this.setCentralizerPcgEntriesFromPcgEntries();
    this.pcgEntryForm.centralizerPcgEntry.patchValue(pcgEntry);
    this.centralizerCreated.emit();
  }

  resetCentralizerPcgEntry(event: TiimeOptionSelectionChange): void {
    if (!event?.isUserInput) {
      return;
    }
    this.pcgEntryForm.resetCentralizerPcgEntryForm();
  }

  updateCentralizer(): void {
    if (this.pcgEntryForm.centralizer.value) {
      this.pcgEntryForm.resetCentralizerPcgEntryForm();
    }
    this.emitPcgEntryFormChange();
  }

  updateCentralizerPcgEntry(pcgEntry: PcgEntry): void {
    this.pcgEntryForm.centralizerPcgEntry.patchValue(pcgEntry);
  }

  // On passe ici par des setTimeout car il y a un bug dans la gestion du disabled dans les reactives forms et les mat-slide-toggle
  // https://github.com/angular/components/issues/17985
  private manageCentralizerControl(disableCentralizer: boolean): void {
    if (disableCentralizer) {
      setTimeout(() => this.pcgEntryForm.centralizer.disable(FormUtils.shouldNotEmitEvent));
    } else {
      setTimeout(() => {
        if (!PcgEntryHelper.isAuxiliaryAccountNumber(this.pcgEntryForm.accountNumber.value)) {
          this.pcgEntryForm.centralizer.enable(FormUtils.shouldNotEmitEvent);
        }
      });
    }
  }

  private setCentralizerPcgEntriesFromPcgEntries(): void {
    this.centralizerPcgEntries = this.pcgEntries?.filter((pcgEntry: PcgEntry) => pcgEntry.centralizer === true) ?? [];
    this.filteredCentralizerPcgEntries = this.centralizerPcgEntries;
  }

  private observeAccountNumberChange(): void {
    if (this.accountNumberChangeSubscription) {
      this.accountNumberChangeSubscription.unsubscribe();
    }
    if (!this.pcgEntryForm) {
      return;
    }

    this.accountNumberChangeSubscription = this.pcgEntryForm.accountNumber.valueChanges
      .pipe(
        startWith(this.pcgEntryForm.accountNumber.value),
        distinctUntilChanged(
          (previous, next) =>
            PcgEntryHelper.isAuxiliaryAccountNumber(previous) === PcgEntryHelper.isAuxiliaryAccountNumber(next)
        ),
        tap((accountNumber: string) => {
          this.manageCentralizerControl(this.disableCentralizer);

          if (PcgEntryHelper.isAuxiliaryAccountNumber(accountNumber)) {
            this.pcgEntryForm.centralizer.setValue(false);
          }
        })
      )
      .subscribe();
  }
}
